import React from 'react'
import { Skeleton } from 'antd'
import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'
import { IQuotaAmount } from '@vacationtracker/shared/types/leave-policy'
import { getFormattedDaysAndHours } from '@vacationtracker/shared/functions/get-days-or-hours'
import { LeaveDisplayFormatEnum } from '@vacationtracker/shared/types/display-in-days-hours'

import IntlMessages from '../../../../util/IntlMessages'
import type { DataGridColumnGroup } from '../../../../design-system/DataGrid'
import { Text } from '../../../../design-system/Text'
import { IUserFilteredInfo, IUserLeaveSummary, IUserLeaveSummaryLeaveType } from '../../../../types/users'
import { IGetLeaveTypeList } from '../../../../types/custom-queries'
import { columnHelper } from './tableSetup'
import { COLUMN_IDS, leaveTypeColumnId } from './columnIds'
import { SHARED_COLUMN_WIDTHS, actionsColumn } from './sharedColumns'
import { NameCell } from '../components/NameCell'
import { IRowActionsMenuProps } from '../components/RowActionsMenu'
import { CellShell } from '../components/EditableCell'

type IFormatMessageFn = Parameters<typeof getFormattedDaysAndHours>[0]['formatMessage']

const withGroupDivider = (node: React.ReactNode): React.ReactElement => (
  <>
    <span className="vt-users-col-divider" aria-hidden />
    {node}
  </>
)

const summaryPlaceholder = (summary?: IUserLeaveSummary): React.ReactElement | null => {
  if (!summary || summary.status === 'PENDING') {
    return <Skeleton.Input active size="small" style={{ minWidth: 40, width: 40, height: 16 }} />
  }
  if (summary.status === 'ERROR') {
    return <>-</>
  }
  return null
}

export interface ILeaveDetailsColumnsContext {
  activeLeaveTypes: IGetLeaveTypeList[]
  getSummaries: () => Record<string, IUserLeaveSummary>
  companyPlatform?: string
  getInviteStatus: (user: IUserFilteredInfo) => EmailInviteStatusEnum
  formatMessage: IFormatMessageFn
  rowMenu: Omit<IRowActionsMenuProps, 'user'>
  getLockReason: (user: IUserFilteredInfo, columnId: string) => string | undefined
  onQuotaEdit: (user: IUserFilteredInfo, leaveTypeId: string) => void
  showEnergy?: boolean
  displayUnit: 'days' | 'hours'
  /** Puts what is left under the used days on capped rows. Unlimited rows never carry it: there is
   * no figure to be left of. */
  showBalance?: boolean
}

export function buildLeaveDetailsColumns(context: ILeaveDetailsColumnsContext) {
  const { activeLeaveTypes, getSummaries, companyPlatform, getInviteStatus, formatMessage, rowMenu, getLockReason, onQuotaEdit, showEnergy, displayUnit } = context
  const showBalance = context.showBalance ?? true

  const summaryLeaveType = (user: IUserFilteredInfo, leaveTypeId: string): { summary?: IUserLeaveSummary; leaveType?: IUserLeaveSummaryLeaveType } => {
    const summary = getSummaries()[user.id]
    return { summary, leaveType: summary?.leaveTypes?.find((entry) => entry.leaveTypeId === leaveTypeId) }
  }

  const unitSuffix = formatMessage({ id: displayUnit === 'hours' ? 'dashboard.hourSingle' : 'dashboard.daysSmallCaseSingle' })
  const roundAmount = (value: number): number => Math.round(value * 100) / 100

  const renderAmount = (amount?: IQuotaAmount): string => {
    if (!amount) {
      return '-'
    }
    if (displayUnit === 'hours') {
      return `${roundAmount(amount.raw.hours)}${unitSuffix}`
    }
    return getFormattedDaysAndHours({ leave: amount, formatMessage, displayFormat: LeaveDisplayFormatEnum.short })
  }

  // The shared formatter writes every zero as hours ("0h"), so a figure added up here reads zero in
  // the unit the rest of the column is in.
  const renderFigure = (amount?: IQuotaAmount): string => (amount && amount.raw.days === 0 && amount.raw.hours === 0 ? `0${unitSuffix}` : renderAmount(amount))

  const renderLeaveValue = (leaveType: IUserLeaveSummaryLeaveType | undefined, kind: 'quota' | 'used'): React.ReactElement => {
    if (!leaveType) {
      return (
        <span className="vt-users-leave-na">
          <IntlMessages id="users.table.notApplicable" />
        </span>
      )
    }
    if (kind === 'quota') {
      return leaveType.hasUnlimitedDays ? <IntlMessages id="app.unlimited" /> : <>{renderAmount(leaveType.quotas?.quotaAtYearEnd)}</>
    }
    return (
      <span className="vt-users-leave-used">
        <span>{renderFigure(leaveType.quotas?.used)}</span>
        {showBalance && !leaveType.hasUnlimitedDays && (
          <Text tone="secondary">
            <IntlMessages id="users.leave.leftShort" values={{ left: renderFigure(leaveType.quotas?.remaining) }} />
          </Text>
        )}
      </span>
    )
  }

  return columnHelper.columns([
    columnHelper.accessor('name', {
      id: COLUMN_IDS.name,
      enableHiding: false,
      header: () => <IntlMessages id="app.name" />,
      cell: ({ row }) => <NameCell user={row.original} companyPlatform={companyPlatform} getInviteStatus={getInviteStatus} showEnergy={showEnergy} />,
    }),
    columnHelper.accessor((row) => row.team?.name, {
      id: COLUMN_IDS.team,
      header: () => <IntlMessages id="app.department" />,
      cell: ({ row }) => (
        <div className="vt-users-cell">
          <span className="vt-users-cell-value">{row.original.team?.name}</span>
        </div>
      ),
    }),
    ...activeLeaveTypes.flatMap((leaveType) => [
      columnHelper.display({
        id: leaveTypeColumnId(leaveType.id, 'quota'),
        header: () => withGroupDivider(<IntlMessages id="users.table.quota" />),
        cell: ({ row }) => {
          const { summary, leaveType: userLeaveType } = summaryLeaveType(row.original, leaveType.id)
          const content = summaryPlaceholder(summary) ?? renderLeaveValue(userLeaveType, 'quota')
          if (summary?.status !== 'OK' || !userLeaveType) {
            return withGroupDivider(content)
          }
          const lockReason = getLockReason(row.original, 'quota')
          if (lockReason) {
            return withGroupDivider(<CellShell display={content} lockReason={lockReason} align="end" />)
          }
          return withGroupDivider(<CellShell display={content} affordance="pencil" align="end" onActivate={() => onQuotaEdit(row.original, leaveType.id)} />)
        },
      }),
      // Still the `balance` column id: it is what saved column settings and the export are keyed
      // on, and the cell now carries the balance under what it reads.
      columnHelper.display({
        id: leaveTypeColumnId(leaveType.id, 'balance'),
        header: () => <IntlMessages id="app.used" />,
        cell: ({ row }) => {
          const { summary, leaveType: userLeaveType } = summaryLeaveType(row.original, leaveType.id)
          return summaryPlaceholder(summary) ?? renderLeaveValue(userLeaveType, 'used')
        },
      }),
    ]),
    actionsColumn(rowMenu),
  ])
}

export function leaveDetailsColumnWidths(activeLeaveTypes: IGetLeaveTypeList[]): Record<string, number> {
  const widths: Record<string, number> = {
    ...SHARED_COLUMN_WIDTHS,
  }
  for (const leaveType of activeLeaveTypes) {
    widths[leaveTypeColumnId(leaveType.id, 'quota')] = 110
    widths[leaveTypeColumnId(leaveType.id, 'balance')] = 110
  }
  return widths
}

export function leaveDetailsNumericColumnIds(activeLeaveTypes: IGetLeaveTypeList[]): string[] {
  return activeLeaveTypes.flatMap((leaveType) => [leaveTypeColumnId(leaveType.id, 'quota'), leaveTypeColumnId(leaveType.id, 'balance')])
}

export function leaveDetailsColumnGroups(activeLeaveTypes: IGetLeaveTypeList[]): DataGridColumnGroup[] {
  return activeLeaveTypes.map((leaveType) => ({
    id: `lt:${leaveType.id}`,
    label: (
      <span className="vt-users-band-label">
        <span className="vt-users-band-swatch" style={{ background: leaveType.color }} aria-hidden />
        {leaveType.name}
      </span>
    ),
    columnIds: [leaveTypeColumnId(leaveType.id, 'quota'), leaveTypeColumnId(leaveType.id, 'balance')],
  }))
}
